// frontend/src/components/chat/ChatBubble.js
// Burbuja flotante de chat - Estilo Messenger

import React, { useState, useEffect } from 'react';
import { API_ENDPOINTS, apiRequest } from '../../config/api';
import ChatWindow from './ChatWindow';
import './ChatBubble.css';

const ChatBubble = ({ user }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [isMinimized, setIsMinimized] = useState(false);
    const [unreadCount, setUnreadCount] = useState(0);
    const [position, setPosition] = useState({ x: 20, y: 20 });
    const [isDragging, setIsDragging] = useState(false);

    // Cargar conteo de mensajes no leidos
    useEffect(() => {
        const fetchUnreadCount = async () => {
            try {
                const response = await apiRequest(API_ENDPOINTS.chatConversations);
                if (response.success) {
                    const total = response.data.reduce((acc, conv) => acc + (conv.unread_count || 0), 0);
                    setUnreadCount(total);
                }
            } catch (error) {
                console.error('Error al cargar mensajes no leidos:', error);
            }
        };

        fetchUnreadCount();
        const interval = setInterval(fetchUnreadCount, 30000);
        return () => clearInterval(interval);
    }, []);

    // Drag functionality
    const handleDragStart = (e) => {
        if (e.target.closest('.urbanq-chat-window')) return;
        setIsDragging(true);
    };

    const handleDrag = (e) => {
        if (!isDragging) return;
        const newX = window.innerWidth - e.clientX - 30;
        const newY = window.innerHeight - e.clientY - 30;
        setPosition({
            x: Math.max(10, Math.min(newX, window.innerWidth - 80)),
            y: Math.max(10, Math.min(newY, window.innerHeight - 80))
        });
    };

    const handleDragEnd = () => {
        setIsDragging(false);
    };

    useEffect(() => {
        if (isDragging) {
            document.addEventListener('mousemove', handleDrag);
            document.addEventListener('mouseup', handleDragEnd);
        }
        return () => {
            document.removeEventListener('mousemove', handleDrag);
            document.removeEventListener('mouseup', handleDragEnd);
        };
    }, [isDragging]);

    const toggleChat = () => {
        if (isMinimized) {
            setIsMinimized(false);
        } else {
            setIsOpen(!isOpen);
        }
    };

    const handleMinimize = () => {
        setIsMinimized(true);
    };

    const handleClose = () => {
        setIsOpen(false);
        setIsMinimized(false);
    };

    // No mostrar si no hay usuario o es SuperAdmin
    if (!user || user.role === 'SuperAdmin' || !user.condominium_id) {
        return null;
    }

    return (
        <div
            className="urbanq-chat-bubble-container"
            style={{ right: position.x, bottom: position.y }}
        >
            {/* Ventana de Chat */}
            {isOpen && !isMinimized && (
                <ChatWindow
                    user={user}
                    onMinimize={handleMinimize}
                    onClose={handleClose}
                    onUnreadChange={setUnreadCount}
                />
            )}

            {/* Burbuja */}
            <button
                className={`urbanq-chat-bubble ${isOpen ? 'active' : ''} ${isDragging ? 'dragging' : ''}`}
                onClick={toggleChat}
                onMouseDown={handleDragStart}
                aria-label="Abrir chat"
            >
                {isOpen ? (
                    <svg viewBox="0 0 24 24" fill="currentColor" className="urbanq-chat-bubble-icon">
                        <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
                    </svg>
                ) : (
                    <svg viewBox="0 0 24 24" fill="currentColor" className="urbanq-chat-bubble-icon">
                        <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm0 14H6l-2 2V4h16v12z"/>
                        <path d="M7 9h10v2H7zm0-3h10v2H7zm0 6h7v2H7z"/>
                    </svg>
                )}

                {/* Badge de mensajes no leidos */}
                {unreadCount > 0 && !isOpen && (
                    <span className="urbanq-chat-bubble-badge">
                        {unreadCount > 99 ? '99+' : unreadCount}
                    </span>
                )}
            </button>

            {/* Preview minimizado */}
            {isMinimized && (
                <div className="urbanq-chat-minimized" onClick={() => setIsMinimized(false)}>
                    <div className="urbanq-chat-minimized-content">
                        <span className="urbanq-chat-minimized-icon">💬</span>
                        <span className="urbanq-chat-minimized-text">Chat</span>
                        {unreadCount > 0 && (
                            <span className="urbanq-chat-minimized-badge">{unreadCount}</span>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default ChatBubble;
